import React, { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

// Custom INR Icon Component
const IndianRupeeIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 3h12M6 8h12M6 13l8.5-8M6 13h5.5a4 4 0 110 8H6" />
  </svg>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BillingHistory = () => {
  const { token } = useAuth();
  const [billingCycles, setBillingCycles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBillingHistory();
  }, []);

  const fetchBillingHistory = async () => {
    try {
      const response = await axios.get(`${API}/billing/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBillingCycles(response.data);
    } catch (error) {
      toast.error('Failed to fetch billing history');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const currentCycle = billingCycles.find((cycle) => cycle.status === 'current');
  const completedCycles = billingCycles.filter((cycle) => cycle.status === 'completed');

  return (
    <div className="space-y-6">
      {/* Current Cycle */}
      {currentCycle && (
        <Card className="border-2 border-indigo-200 bg-indigo-50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-indigo-900">Current Billing Cycle (30 Days)</CardTitle>
              <Badge className="bg-indigo-600 text-white">Active</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <div className="p-3 bg-indigo-100 rounded-lg">
                  <Calendar className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-indigo-600 mb-1">Period</p>
                  <p className="text-sm text-indigo-900">
                    {format(new Date(currentCycle.start_date), 'MMM dd, yyyy')} -
                  </p>
                  <p className="text-sm text-indigo-900">
                    {format(new Date(currentCycle.end_date), 'MMM dd, yyyy')}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-3 bg-indigo-100 rounded-lg">
                  <FileText className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-indigo-600 mb-1">Total Tickets</p>
                  <p className="text-2xl font-bold text-indigo-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {currentCycle.ticket_count}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-3 bg-indigo-100 rounded-lg">
                  <IndianRupeeIcon className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-indigo-600 mb-1">Total Revenue</p>
                  <p className="text-2xl font-bold text-indigo-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    ₹{currentCycle.total_amount.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Historical Cycles */}
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Previous Billing Cycles</h3>
        {completedCycles.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="text-slate-400 mb-4">
                <Calendar className="w-20 h-20" />
              </div>
              <p className="text-slate-500 text-lg mb-2">No previous billing cycles</p>
              <p className="text-slate-400 text-sm">Historical data will appear here</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {completedCycles.map((cycle) => (
              <Card key={cycle.id} className="card-hover" data-testid={`billing-cycle-${cycle.id}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Calendar className="w-5 h-5 text-slate-500" />
                        <h4 className="font-semibold text-slate-900">
                          {format(new Date(cycle.start_date), 'MMM dd')} -{' '}
                          {format(new Date(cycle.end_date), 'MMM dd, yyyy')}
                        </h4>
                        <Badge variant="outline" className="text-xs">
                          Completed
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600">
                        {cycle.ticket_count} ticket{cycle.ticket_count !== 1 ? 's' : ''} processed
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500 mb-1">Total Revenue</p>
                      <p className="text-3xl font-bold text-indigo-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        ₹{cycle.total_amount.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Overall Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium text-slate-500 mb-2">Total Cycles</p>
              <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {billingCycles.length}
              </p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium text-slate-500 mb-2">Total Tickets</p>
              <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {billingCycles.reduce((sum, cycle) => sum + cycle.ticket_count, 0)}
              </p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium text-slate-500 mb-2">Total Revenue</p>
              <p className="text-3xl font-bold text-indigo-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                ₹{billingCycles.reduce((sum, cycle) => sum + cycle.total_amount, 0).toFixed(2)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingHistory;
